import React, { Component } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Link,
  Button,
  UnorderedList,
  ListItem,
  Divider
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import './PrivacyPolicy.css';

class PrivacyPolicy extends Component {
  componentDidMount() {
    window.scrollTo(0, 0);
  }

  render() {
    return (
      <Box bg="gray.50" minH="100vh" py={12}>
        <Container maxW="container.md">
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="link"
            mb={8}
            onClick={() => this.props.onRouteChange('landing')}
            color="blue.600"
            fontSize="sm"
          >
            Terug naar home
          </Button>

          <Box bg="white" borderRadius="xl" boxShadow="lg" p={8}>
            <VStack align="stretch" spacing={10}>
            {/* Header */}
            <Box borderBottom="1px solid" borderColor="gray.300" pb={4}>
              <Heading as="h1" size="2xl" color="gray.900" mb={2} fontWeight="700">
                Privacybeleid
              </Heading>
              <Text color="gray.600" fontSize="sm">
                Laatst bijgewerkt: {new Date().toLocaleDateString('nl-NL')}
              </Text>
            </Box>

            {/* 1. Inleiding */}
            <Box>
              <Heading as="h2" size="lg" color="gray.900" mb={4} fontWeight="600">
                1. Inleiding
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={3}>
                Demos hecht veel waarde aan de bescherming van uw persoonsgegevens.
                In dit privacybeleid leggen wij uit welke persoonsgegevens wij verzamelen,
                waarom wij deze verzamelen, hoe wij deze gebruiken en welke rechten u heeft
                met betrekking tot uw gegevens.
              </Text>
              <Text color="gray.700" lineHeight="tall">
                Dit privacybeleid is opgesteld in overeenstemming met de Algemene Verordening
                Gegevensbescherming (AVG).
              </Text>
            </Box>

            {/* 2. Verwerkingsverantwoordelijke */}
            <Box>
              <Heading as="h2" size="lg" color="gray.900" mb={4} fontWeight="600">
                2. Verwerkingsverantwoordelijke
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={3}>
                De verwerkingsverantwoordelijke voor de verwerking van uw persoonsgegevens is:
              </Text>
              <Box pl={4} borderLeft="3px solid" borderColor="gray.300">
                <Text fontWeight="600" color="gray.800">Demos</Text>
                <Text fontSize="sm" color="gray.600">
                  E-mail privacyvragen: <Link href="mailto:privacy@metdemos.app" color="blue.600" textDecoration="underline">privacy@metdemos.app</Link>
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Algemene vragen: <Link href="mailto:info@metdemos.app" color="blue.600" textDecoration="underline">info@metdemos.app</Link>
                </Text>
                <Text fontSize="sm" color="gray.600" mt={3} fontWeight="500">
                  Voor vragen over uw gemeente:
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Neem contact op met de Functionaris Gegevensbescherming (FG) van uw gemeente voor vragen over hoe uw gemeente uw gegevens verwerkt via Demos.
                </Text>
                <Text fontSize="sm" color="gray.600" mt={3} fontWeight="500">
                  Klacht indienen:
                </Text>
                <Text fontSize="sm" color="gray.600">
                  U kunt een klacht over privacy indienen bij de Autoriteit Persoonsgegevens via{' '}
                  <Link
                    href="https://autoriteitpersoonsgegevens.nl/nl/zelf-doen/gebruik-uw-privacyrechten/klacht-melden-bij-de-ap"
                    color="blue.600"
                    textDecoration="underline"
                    isExternal
                  >
                    autoriteitpersoonsgegevens.nl
                  </Link>
                </Text>
              </Box>
            </Box>

            {/* 3. Welke gegevens verzamelen wij */}
            <Box>
              <Heading as="h2" size="lg" color="gray.900" mb={4} fontWeight="600">
                3. Welke gegevens verzamelen wij?
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                Wij verzamelen de volgende categorieën persoonsgegevens:
              </Text>

              <VStack align="stretch" spacing={3}>
                <Box>
                  <Text fontWeight="600" color="gray.800" mb={1}>Accountgegevens</Text>
                  <Text fontSize="sm" color="gray.600">
                    Gebruikersnaam, e-mailadres, wachtwoord (gehashed)
                  </Text>
                </Box>
                <Box>
                  <Text fontWeight="600" color="gray.800" mb={1}>Suggesties</Text>
                  <Text fontSize="sm" color="gray.600">
                    Tekst van ingediende suggesties, datum van indiening, status
                  </Text>
                </Box>
                <Box>
                  <Text fontWeight="600" color="gray.800" mb={1}>Technische gegevens</Text>
                  <Text fontSize="sm" color="gray.600">
                    IP-adres (voor beveiliging en audit), browsertype, apparaatinformatie (via cookies)
                  </Text>
                </Box>
                <Box>
                  <Text fontWeight="600" color="gray.800" mb={1}>Privacyvoorkeuren</Text>
                  <Text fontSize="sm" color="gray.600">
                    Toestemming voor AI-verwerking van suggesties, cookie voorkeuren
                  </Text>
                </Box>
              </VStack>
            </Box>

            {/* 4. Waarom verwerken wij uw gegevens */}
            <Box>
              <Heading as="h2" size="lg" color="gray.900" mb={4} fontWeight="600">
                4. Waarom verwerken wij uw gegevens?
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                Wij verwerken uw persoonsgegevens voor de volgende doeleinden:
              </Text>

              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="600" color="gray.800" mb={2}>
                    a) Uitvoering van de overeenkomst (AVG art. 6 lid 1 sub b)
                  </Text>
                  <UnorderedList spacing={1} pl={4} color="gray.700" fontSize="sm">
                    <ListItem>Het aanmaken en beheren van uw account</ListItem>
                    <ListItem>Het mogelijk maken om suggesties in te dienen</ListItem>
                    <ListItem>Het tonen van ingediende suggesties aan vertegenwoordigers</ListItem>
                    <ListItem>Communicatie tussen burgers en vertegenwoordigers</ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Text fontWeight="600" color="gray.800" mb={2}>
                    b) Toestemming (AVG art. 6 lid 1 sub a)
                  </Text>
                  <UnorderedList spacing={1} pl={4} color="gray.700" fontSize="sm">
                    <ListItem>
                      AI-verwerking van suggesties via externe AI-diensten (zoals OpenAI) voor het genereren
                      van samenvattingen (alleen als u hier expliciet toestemming voor heeft gegeven)
                    </ListItem>
                    <ListItem>Plaatsen en uitlezen van analytische cookies</ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Text fontWeight="600" color="gray.800" mb={2}>
                    c) Gerechtvaardigd belang (AVG art. 6 lid 1 sub f)
                  </Text>
                  <UnorderedList spacing={1} pl={4} color="gray.700" fontSize="sm">
                    <ListItem>
                      Trending issues analyse via lokale verwerking (geen externe AI-diensten)
                      om belangrijke thema's te identificeren voor vertegenwoordigers
                    </ListItem>
                    <ListItem>Verbetering van de dienstverlening en gebruikerservaring</ListItem>
                    <ListItem>Beveiliging van het platform tegen misbruik</ListItem>
                    <ListItem>Analyse van gebruiksstatistieken (geanonimiseerd)</ListItem>
                  </UnorderedList>
                </Box>
              </VStack>
            </Box>

            {/* 5. Met wie delen wij uw gegevens */}
            <Box>
              <Heading as="h2" size="lg" color="gray.900" mb={4} fontWeight="600">
                5. Met wie delen wij uw gegevens?
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                Wij delen uw persoonsgegevens alleen in de volgende gevallen:
              </Text>

              <UnorderedList spacing={3} pl={4} color="gray.700">
                <ListItem>
                  <Text as="span" fontWeight="600">Vertegenwoordigers: </Text>
                  Uw gebruikersnaam en ingediende suggesties zijn zichtbaar voor vertegenwoordigers
                  om deze te kunnen beoordelen en beantwoorden. Suggesties worden ook gebruikt voor
                  trending issues analyse (lokale verwerking, geen externe partijen).
                </ListItem>
                <ListItem>
                  <Text as="span" fontWeight="600">AI-dienstverleners (alleen met uw toestemming): </Text>
                  Als u toestemming heeft gegeven, worden uw suggesties (zonder direct identificerende
                  informatie) verwerkt door externe AI-diensten (zoals OpenAI) voor het genereren van
                  geautomatiseerde samenvattingen.
                </ListItem>
                <ListItem>
                  <Text as="span" fontWeight="600">Hosting providers: </Text>
                  Onze servers en databases worden gehost door betrouwbare hostingpartijen die voldoen
                  aan AVG-eisen.
                </ListItem>
              </UnorderedList>

              <Box mt={4} p={3} bg="blue.50" borderRadius="md">
                <Text fontSize="sm" color="blue.900">
                  <Text as="span" fontWeight="600">Let op: </Text>
                  Wij verkopen of verhuren uw gegevens nooit aan derden voor marketingdoeleinden.
                </Text>
              </Box>
            </Box>

            {/* 6. Bewaartermijnen */}
            <Box>
              <Heading as="h2" size="lg" color="gray.900" mb={4} fontWeight="600">
                6. Hoe lang bewaren wij uw gegevens?
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                Wij bewaren uw persoonsgegevens niet langer dan noodzakelijk:
              </Text>

              <VStack align="stretch" spacing={2}>
                <Box display="flex" justifyContent="space-between" py={2} borderBottom="1px solid" borderColor="gray.200">
                  <Text fontSize="sm" color="gray.700" fontWeight="600">Accountgegevens</Text>
                  <Text fontSize="sm" color="gray.600">Tot verwijdering + 30 dagen</Text>
                </Box>
                <Box display="flex" justifyContent="space-between" py={2} borderBottom="1px solid" borderColor="gray.200">
                  <Text fontSize="sm" color="gray.700" fontWeight="600">Suggesties</Text>
                  <Text fontSize="sm" color="gray.600">Tot verwijdering + 30 dagen</Text>
                </Box>
                <Box display="flex" justifyContent="space-between" py={2} borderBottom="1px solid" borderColor="gray.200">
                  <Text fontSize="sm" color="gray.700" fontWeight="600">Berichten</Text>
                  <Text fontSize="sm" color="gray.600">Tot verwijdering + 30 dagen</Text>
                </Box>
                <Box display="flex" justifyContent="space-between" py={2} borderBottom="1px solid" borderColor="gray.200">
                  <Text fontSize="sm" color="gray.700" fontWeight="600">Audit logs</Text>
                  <Text fontSize="sm" color="gray.600">1 jaar</Text>
                </Box>
                <Box display="flex" justifyContent="space-between" py={2}>
                  <Text fontSize="sm" color="gray.700" fontWeight="600">Backups</Text>
                  <Text fontSize="sm" color="gray.600">Maximum 90 dagen</Text>
                </Box>
              </VStack>
            </Box>

            {/* 7. Uw rechten */}
            <Box>
              <Heading as="h2" size="lg" color="gray.900" mb={4} fontWeight="600">
                7. Uw rechten
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                Op grond van de AVG heeft u de volgende rechten met betrekking tot uw persoonsgegevens:
              </Text>

              <VStack align="stretch" spacing={3}>
                <Box>
                  <Text fontWeight="600" color="gray.800" fontSize="sm">
                    Recht op inzage (art. 15 AVG)
                  </Text>
                  <Text color="gray.600" fontSize="sm" lineHeight="tall">
                    U kunt via de "Data Export" functie in uw instellingen al uw gegevens downloaden
                    in JSON of CSV formaat.
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="600" color="gray.800" fontSize="sm">
                    Recht op rectificatie (art. 16 AVG)
                  </Text>
                  <Text color="gray.600" fontSize="sm" lineHeight="tall">
                    U kunt onjuiste of onvolledige gegevens laten corrigeren via uw accountinstellingen.
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="600" color="gray.800" fontSize="sm">
                    Recht op vergetelheid (art. 17 AVG)
                  </Text>
                  <Text color="gray.600" fontSize="sm" lineHeight="tall">
                    U kunt uw account en alle bijbehorende gegevens verwijderen via de "Account Verwijderen"
                    functie in uw instellingen. Na verwijdering heeft u 30 dagen om dit te annuleren.
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="600" color="gray.800" fontSize="sm">
                    Recht op dataportabiliteit (art. 20 AVG)
                  </Text>
                  <Text color="gray.600" fontSize="sm" lineHeight="tall">
                    U kunt uw gegevens in een gestructureerd, gangbaar formaat ontvangen (JSON of CSV).
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="600" color="gray.800" fontSize="sm">
                    Recht om toestemming in te trekken (art. 7 lid 3 AVG)
                  </Text>
                  <Text color="gray.600" fontSize="sm" lineHeight="tall">
                    U kunt uw toestemming voor AI-verwerking op elk moment intrekken via uw privacy-instellingen.
                  </Text>
                </Box>
              </VStack>

              <Box mt={4} p={3} bg="green.50" borderRadius="md">
                <Text fontSize="sm" color="green.900" fontWeight="600" mb={1}>
                  Hoe maakt u gebruik van uw rechten?
                </Text>
                <Text fontSize="sm" color="green.800" lineHeight="tall">
                  De meeste rechten kunt u zelf uitoefenen via uw accountinstellingen.
                  Voor andere verzoeken kunt u contact opnemen via privacy@demos.nl.
                  Wij reageren binnen 30 dagen op uw verzoek.
                </Text>
              </Box>
            </Box>

            {/* 8. Beveiliging */}
            <Box>
              <Heading as="h2" size="lg" color="gray.900" mb={4} fontWeight="600">
                8. Beveiliging
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                Wij nemen de beveiliging van uw gegevens serieus en treffen passende
                technische en organisatorische maatregelen:
              </Text>

              <UnorderedList spacing={2} pl={4} color="gray.700" fontSize="sm">
                <ListItem>Wachtwoorden worden versleuteld opgeslagen met Argon2id-algoritme</ListItem>
                <ListItem>Alle communicatie verloopt via beveiligde HTTPS-verbindingen</ListItem>
                <ListItem>Toegang tot persoonsgegevens is beperkt tot geautoriseerd personeel</ListItem>
                <ListItem>Reguliere beveiligingsaudits en updates van systemen</ListItem>
                <ListItem>CSRF-bescherming voor alle gevoelige acties</ListItem>
                <ListItem>Audit logging van toegang tot gevoelige gegevens door vertegenwoordigers</ListItem>
              </UnorderedList>
            </Box>

            {/* 9. Cookies */}
            <Box>
              <Heading as="h2" size="lg" color="gray.900" mb={4} fontWeight="600">
                9. Cookies
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                Onze website gebruikt cookies. Een cookie is een klein tekstbestand dat bij het
                eerste bezoek aan deze website wordt opgeslagen in de browser van uw computer.
              </Text>

              <Heading as="h3" size="md" color="gray.800" mb={2} mt={4}>
                Functionele cookies
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={2} fontSize="sm">
                Deze cookies zijn noodzakelijk voor het goed functioneren van de website:
              </Text>
              <UnorderedList spacing={1} pl={4} color="gray.700" fontSize="sm" mb={4}>
                <ListItem>Sessiecookies voor inloggen</ListItem>
                <ListItem>CSRF-tokens voor beveiliging</ListItem>
                <ListItem>Cookie-voorkeurscookie</ListItem>
              </UnorderedList>

              <Heading as="h3" size="md" color="gray.800" mb={2} mt={4}>
                Analytische cookies
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={2} fontSize="sm">
                Met uw toestemming gebruiken wij analytische cookies om fouten op te sporen en het gebruik van de website te analyseren.
              </Text>
            </Box>

            {/* 10. Wijzigingen */}
            <Box>
              <Heading as="h2" size="lg" color="gray.900" mb={4} fontWeight="600">
                10. Wijzigingen in dit privacybeleid
              </Heading>
              <Text color="gray.700" lineHeight="tall">
                Wij kunnen dit privacybeleid van tijd tot tijd aanpassen. De meest recente versie
                is altijd te vinden op deze pagina. Bij belangrijke wijzigingen zullen wij u
                hiervan op de hoogte stellen via e-mail of een melding op de website.
              </Text>
            </Box>

            {/* 11. Klachten */}
            <Box>
              <Heading as="h2" size="lg" color="gray.900" mb={4} fontWeight="600">
                11. Klachten
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={3}>
                Als u een klacht heeft over de verwerking van uw persoonsgegevens, neem dan
                eerst contact met ons op via privacy@metdemos.app. U heeft ook het recht om
                een klacht in te dienen bij de Autoriteit Persoonsgegevens:
              </Text>

              <Box pl={4} borderLeft="3px solid" borderColor="gray.300">
                <Text fontWeight="600" mb={1}>Autoriteit Persoonsgegevens</Text>
                <Text fontSize="sm" color="gray.600">Postbus 93374</Text>
                <Text fontSize="sm" color="gray.600">2509 AJ Den Haag</Text>
                <Text fontSize="sm" color="gray.600">Telefoon: 088 - 1805 250</Text>
                <Text fontSize="sm" color="gray.600">
                  Website:{' '}
                  <Link href="https://autoriteitpersoonsgegevens.nl" isExternal color="blue.600">
                    autoriteitpersoonsgegevens.nl
                  </Link>
                </Text>
              </Box>
            </Box>

            {/* 12. Contact */}
            <Box>
              <Heading as="h2" size="lg" color="gray.900" mb={4} fontWeight="600">
                12. Contact
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={3}>
                Heeft u vragen over dit privacybeleid of over de verwerking van uw persoonsgegevens?
                Neem dan contact met ons op:
              </Text>

              <Box pl={4} borderLeft="3px solid" borderColor="blue.400">
                <Text fontWeight="600">Demos - Privacy</Text>
                <Text fontSize="sm" color="gray.700">E-mail: privacy@metdemos.app</Text>
                <Text fontSize="sm" color="gray.700" mt={2}>
                  Wij streven ernaar om binnen 48 uur te reageren op uw vraag.
                </Text>
              </Box>
            </Box>

            <Divider />

            {/* Back button */}
            <Box textAlign="center" pt={4}>
              <Button
                leftIcon={<ArrowBackIcon />}
                colorScheme="blue"
                onClick={() => this.props.onRouteChange('landing')}
                size="lg"
              >
                Terug naar home
              </Button>
            </Box>
          </VStack>
          </Box>
        </Container>
      </Box>
    );
  }
}

export default PrivacyPolicy;
